import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { MIN_PASSWORD_LENGTH } from "@/config/passwordPolicy";
import { apiClient } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/layout/Navbar";

export default function ChangePasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();
  const { setSession } = useAuth();

  const changePasswordToken = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});

  const validate = (): boolean => {
    const next: typeof errors = {};

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      next.newPassword = t("auth:passwordTooShort");
    }
    if (newPassword !== confirmPassword) {
      next.confirmPassword = t("auth:passwordMismatch");
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    if (!changePasswordToken) {
      toast.error(t("auth:invalidChangePasswordToken"));
      return;
    }

    setIsLoading(true);
    try {
      const { user, token, refreshToken } = await apiClient.changePassword({
        changePasswordToken,
        newPassword,
      });

      setSession(user, token, refreshToken);

      toast.success(t("auth:passwordChanged"));
      navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      const apiError = error as {
        response?: { data?: { error?: { message?: string }; message?: string } };
      };
      const errorMessage =
        apiError.response?.data?.error?.message ||
        apiError.response?.data?.message ||
        t("auth:loginError");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
 <div className="min-h-screen bg-secondary-50 flex flex-col">
      <Navbar hideSidebarButtons={true} hideLoginLink={true} />
 <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
 <div className="max-w-md w-full space-y-8">
          <div>
 <div className="flex justify-center">
 <KeyRound className="h-12 w-12 text-primary-600" />
            </div>
 <h2 className="mt-6 text-center text-3xl font-extrabold text-secondary-900">
              {t("auth:changePassword")}
            </h2>
 <p className="mt-2 text-center text-sm text-secondary-600">
              {t("auth:changePasswordDescription")}
            </p>
          </div>

 <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
 <div className="space-y-4">
              <div>
                <label
                  htmlFor="newPassword"
 className="block text-sm font-medium text-secondary-700"
                >
                  {t("auth:newPassword")}
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
 className="input mt-1"
                  placeholder={t("auth:newPassword")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
 <p className="mt-1 text-xs text-secondary-500">
                  {t("auth:passwordRequirement")}
                </p>
                {errors.newPassword && (
 <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
 className="block text-sm font-medium text-secondary-700"
                >
                  {t("auth:confirmPassword")}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
 className="input mt-1"
                  placeholder={t("auth:confirmPassword")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {errors.confirmPassword && (
 <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
 className="btn-primary w-full py-2 px-4 disabled:opacity-50"
              >
                {isLoading ? (
 <div className="flex items-center justify-center">
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t("common:loading")}
                  </div>
                ) : (
                  t("auth:changePassword")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
